// ── ONE rail-aware LC-disposition authority (400) ────────────────────────────────────────────
// Extracted from 394's DispositionSection account_from split, per USER-LEGIBLE-TRUTH
// (C-PERM_DECISIONS_LEDGER §0): every surface READS this ONE authority — it does not each
// self-compute a contradicting figure from raw lc_amount_owed.
//
// THE RAIL RULE (394 §3b, evidence 398): an LC cancellation / overpay is classified by the
// account_from ROLE of its funding / cancellation leg:
//   - Bank / cash  account_from  → CASH rail  → the overpay was ALREADY reversed to bank
//       (LC Holding nets 0). SETTLED — no open refund, no apply-forward, NOT actionable.
//       (150852's 112: Bank → LC Holding — refunded to bank.)
//   - Supplier / partner-AP       → CREDIT rail → a real apply-forward credit minted on the
//       supplier's on-account pool. OPEN — actionable. (151035's Supplier A 1,004.84.)
//
// This is the same disease the 383→392 goods-badge arc fixed, now on the LC / cash axis: raw
// lc_amount_owed (−112) is a sticky engine artifact (383) that does NOT know the bank reversal
// already settled it. The rail does. Surfaces must read the rail, not the raw negative owed.

export const LC_RAIL = { CASH: 'cash', CREDIT: 'credit', UNKNOWN: 'unknown' };

// Normalize a rail signal from ANY of the surfaces' available fields:
//   - engine lc_line    → { lcIsCash }        (server.js:9214 lc_is_cash, from account_from bank/cash)
//   - finance leg (394) → { accountFromType }  (accounts.type on the cancellation leg — server.js:12979)
//   - landed_cost meta  → { accountFromType }
// The explicit engine boolean wins; else fall back to the account_from type string.
export function lcRail({ lcIsCash, accountFromType } = {}) {
  if (lcIsCash === true) return LC_RAIL.CASH;
  const t = String(accountFromType || '').toLowerCase();
  if (t === 'bank' || t === 'cash') return LC_RAIL.CASH;
  if (t === 'supplier') return LC_RAIL.CREDIT;
  if (lcIsCash === false) return LC_RAIL.CREDIT;   // engine says not-cash ⇒ credit (supplier-funded)
  return LC_RAIL.UNKNOWN;
}

// THE core classifier — maps a rail to the canonical disposition state. This is the ONE place the
// cash-vs-credit outcome is decided; 394's disposition box, the payment status panel and the
// Manage-LC tab all read it, so they cannot contradict each other.
//   kind           : 'settled_cash' | 'open_credit' | 'unknown'
//   actionable     : does the user have an action to take (apply / collect)?
//   applyForward   : is there an on-account apply-forward credit (drives 394's [Apply credits])?
//   refundedToBank : is the money already back in bank (a settled cash reversal)?
export function classifyLcRail(rail) {
  if (rail === LC_RAIL.CASH)
    return { kind: 'settled_cash', actionable: false, applyForward: false, refundedToBank: true };
  if (rail === LC_RAIL.CREDIT)
    return { kind: 'open_credit', actionable: true, applyForward: true, refundedToBank: false };
  // UNKNOWN → preserve pre-400 behavior (treat as a genuine open position); never a false "settled".
  return { kind: 'unknown', actionable: true, applyForward: false, refundedToBank: false };
}

// The "refund due" surfaces (payment status panel + Manage-LC tab) read this: given ONE engine
// lc_line with a SIGNED lc_amount_owed, it returns what the owed / refund cell should DISPLAY, so a
// settled cash reversal renders "refunded to bank" (settled), and a cancelled-and-released credit
// renders "cancellation credit → pool" — instead of a false "refund due".
//   state       : 'released_to_pool' | 'refunded_to_bank' | 'refund_due' | 'owed' | 'settled'
//   amount      : magnitude to show (for released_to_pool this is the GROSS cancellation credit,
//                 = the LC's own lc_cancelled — NOT the stale raw owed, NOT a pool balance)
//   owedDisplay : what the LC's OWED cell should read (0 for released_to_pool / refunded_to_bank —
//                 the obligation is resolved; the raw −1005 is 383's never-decrementing artifact)
//   applyCredits: does this state hand off to the payments Apply-credit surface (pool)?
//   actionable
//
// 403 (credit-rail sibling of 400's cash-rail fix): 400's supplier-AP branch was TOO COARSE — it read
// EVERY credit-rail owed<0 as an "open refund due". But a CANCELLED credit LC has already minted an
// on-account credit on the supplier's pool (the cancellation books a negative landed_cost leg to the
// supplier AP) — that is RELEASED-TO-POOL (past-tense, resolved), NOT a genuine open cash refund. The
// concrete, provable signal on the lc_line is lc_cancelled>0 on the credit rail (per DISPOSITION-ROW-
// SHOWS-GROSS-NOT-REMAINING: the LC row states the GROSS this order made available; the pool / payments
// page owns "what's currently applicable"). A credit-rail owed<0 with NO cancellation (lc_cancelled≈0),
// or an unknown-rail owed<0, stays a GENUINE open "refund due to us".
export function lcOwedDisplay(line) {
  const owed = Number(line?.lc_amount_owed) || 0;
  const cancelled = Number(line?.lc_cancelled) || 0;
  if (owed < -0.01) {
    const c = classifyLcRail(lcRail({ lcIsCash: line?.lc_is_cash, accountFromType: line?.account_from_type }));
    if (c.kind === 'settled_cash')
      return { state: 'refunded_to_bank', amount: Math.abs(owed), owedDisplay: 0, applyCredits: false, actionable: false };
    // CREDIT rail + a real cancellation → the credit was released to the supplier's on-account pool.
    // GROSS shown = the LC's own lc_cancelled (402-confirmed genuine; LC-A1 603 / LC-A2 402).
    if (c.kind === 'open_credit' && cancelled > 0.01)
      return { state: 'released_to_pool', amount: cancelled, owedDisplay: 0, applyCredits: true, actionable: true };
    // Genuine open refund (credit rail with no cancellation, or unknown rail) — keep "refund due".
    return { state: 'refund_due', amount: Math.abs(owed), owedDisplay: Math.abs(owed), applyCredits: false, actionable: true };
  }
  if (owed > 0.01) return { state: 'owed', amount: owed, owedDisplay: owed, applyCredits: false, actionable: true };
  return { state: 'settled', amount: 0, owedDisplay: 0, applyCredits: false, actionable: false };
}

// The per-LC PAYMENT BADGE on the Manage-LC panel currently reads lc_payment_status (the P1 axis),
// which stickily shows 'Refund Due' (a 383/401 artifact) even for a released-to-pool credit or a
// settled cash reversal. This maps an lc_line to the badge it SHOULD show, from the SAME rail
// authority as the stat line — so the badge and the stat line AGREE (no divergent copy). It ONLY
// overrides the stale 'refund_due' badge; every other P1 value (paid/partial/unpaid/void) falls
// through to the default PaymentBadge mapping (returns null here).
//   → null                                  : not a refund_due badge — use the default PaymentBadge(p1)
//   → { kind, label }  with label === null  : settled (cash reversal / resolved) — render NO chip
//   → { kind:'released_to_pool', label }    : credit released to pool — apply-forward chip
//   → { kind:'refund_due',       label }    : genuine open refund — keep the red 'Refund Due' chip
export function lcRefundBadge(line) {
  // 413: refund_due is no longer emitted on the PAYMENT axis (lc_payment_status), so the old
  // `lc_payment_status === 'refund_due'` guard would never fire. Repoint onto the DISPOSITION-axis
  // truth (lcOwedDisplay(line).state, derived from the signed lc_amount_owed + the rail) so the 403
  // per-LC badge SURVIVES — byte-identical outcome, just sourced from the disposition state not the
  // payment label. Partition is identical to the old guard: an owed<0 line (formerly refund_due-
  // labeled) yields released_to_pool / refunded_to_bank / refund_due; an owed≥0 line yields owed /
  // settled → null → default PaymentBadge(p1).
  const d = lcOwedDisplay(line);
  if (d.state === 'released_to_pool') return { kind: 'released_to_pool', label: 'Credit released' };
  if (d.state === 'refund_due')       return { kind: 'refund_due', label: 'Refund Due' };
  if (d.state === 'refunded_to_bank') return { kind: 'refunded_to_bank', label: null };  // settled cash reversal → no chip (unchanged)
  // owed / settled → not a refund position; use the default PaymentBadge(p1).
  return null;
}

// Aggregate lcOwedDisplay across an order's lc_lines — the payment panel shows ONE combined LC row,
// so it needs the raw negative owed split into a GENUINE open refund vs an already-SETTLED cash
// reversal (→ "refunded to bank X") vs a cancelled-and-released credit (→ pool, resolved).
export function lcOwedAggregate(lcLines) {
  let owed = 0, refundDue = 0, refundedToBank = 0, releasedToPool = 0;
  for (const l of lcLines || []) {
    const d = lcOwedDisplay(l);
    if (d.state === 'owed') owed += d.amount;
    else if (d.state === 'refund_due') refundDue += d.amount;
    else if (d.state === 'refunded_to_bank') refundedToBank += d.amount;
    else if (d.state === 'released_to_pool') releasedToPool += d.amount;
  }
  return { owed, refundDue, refundedToBank, releasedToPool };
}

// ── 401: the order-page SECOND-AXIS (disposition) badge ──────────────────────────────────────────
// The order page shows a DELIVERY axis and a PAYMENT axis at badge level, but the refund-DISPOSITION
// axis (goods refunds/credits/write-offs + LC cancellations) was invisible there — it lived ONLY in
// the "Refund disposition" section below. So an order's disposition (e.g. 150852's LC refunded-to-bank,
// 151035's LC cancellation credit) never showed at the badge level, while the raw sticky order-level
// payment_status='refund_due' (a 383/401 engine artifact) would CONTRADICT the section if surfaced.
//
// This summarizes the order's refund disposition into ONE badge, reading the SAME authority the
// DispositionSection reads — the LC rail helper (lcOwedAggregate → lcOwedDisplay → classifyLcRail) over
// the LC positions, and the SAME 388 disposition_positions tags the section's goods rows read — so the
// badge can never contradict the section (both read this file for LC + the same tag source for goods).
//
// 409 — MULTI-STATE "+N" pill, MOST-ACTIONABLE headline (owner-ruled option (b), 2026-07-28). The old
// helper RETURNED ON FIRST MATCH by a fixed LC-first precedence, so it HID every other disposition: on
// 151035 a genuinely COLLECTABLE 2,945.45 goods refund (pullable to bank now) sat invisible behind the
// passive LC "Credit released" pill. The fix: COLLECT ALL distinct disposition states on the order (LC
// axis + goods axis), HEADLINE the MOST-ACTIONABLE one, and, when more than one distinct state is present,
// return extraCount = (distinct states − 1) so the pill can render "+N" — nothing is ever hidden.
//
// ACTIONABILITY ORDER (most-actionable first — REPLACES the old LC-first precedence), across BOTH axes:
//   1 refund_available (goods) 'Refund collectable' — pullable to bank NOW (most actionable)
//   2 refundDue        (LC)    'Refund due'         — an open refund owed to us
//   3 releasedToPool   (LC)    'Credit released'    — a pooled credit, applies later (semi-actionable)
//   4 cancelled_to_pool(goods) 'Credit available'   — pooled credit, applies later
//   5 stranded         (goods) 'Refund stranded'    — needs attention, not a clean action
//   6 refundedToBank   (LC)    'Refunded to bank'   — settled, informational
//   7 written_off      (goods) 'Written off'        — settled/terminal, informational
// Per-state labels/tones are UNCHANGED from 401 — only the SELECTION (headline) + the +N are new. Distinct
// STATES are counted, not events (two goods write-offs = one 'Written off' state).
//   → null                                  : no disposition — render no pill (unchanged)
//   → { kind, label, tone, extraCount }      : headline state; extraCount = other distinct states (0 = single)
export function orderDispositionBadge({ lcLines = [], dispositionPositions = [] } = {}) {
  // LC axis (the SAME lcOwedAggregate the payment panel reads; same rail authority as the section's LC rows).
  const agg = lcOwedAggregate(lcLines);
  // Goods axis (the SAME 388 disposition_positions tags the section's goods rows render).
  const tags = new Set();
  for (const p of dispositionPositions || []) for (const t of (p.tags || [])) tags.add(t);

  // Ranked catalogue (most-actionable first). Each entry is present iff its predicate holds; the headline
  // is the first present entry, and extraCount counts the rest.
  const catalogue = [
    { present: tags.has('refund_available'),  kind: 'refund_available',  label: 'Refund collectable', tone: 'open' },
    { present: agg.refundDue > 0.01,          kind: 'refund_due',        label: 'Refund due',         tone: 'open' },
    { present: agg.releasedToPool > 0.01,     kind: 'released_to_pool',  label: 'Credit released',    tone: 'open' },
    { present: tags.has('cancelled_to_pool'), kind: 'cancelled_to_pool', label: 'Credit available',   tone: 'open' },
    { present: tags.has('stranded'),          kind: 'stranded',          label: 'Refund stranded',    tone: 'open' },
    { present: agg.refundedToBank > 0.01,     kind: 'refunded_to_bank',  label: 'Refunded to bank',   tone: 'resolved' },
    { present: tags.has('written_off'),       kind: 'written_off',       label: 'Written off',        tone: 'resolved' },
    // 710 Part 4 — RENDER PARITY. These two were ruled ONTO this axis by 417 and given FILTER options
    // by 703 Part 4, but never a badge: filterable and unrenderable. Now both render.
    { present: tags.has('partially_cancelled'), kind: 'partially_cancelled', label: 'Partially cancelled', tone: 'resolved' },
    { present: tags.has('cancelled'),          kind: 'cancelled',         label: 'Cancelled',          tone: 'resolved' },
  ];
  const present = catalogue.filter((s) => s.present);
  if (present.length === 0) return null;                 // no disposition → no pill (unchanged)
  const [headline, ...others] = present;
  return {
    kind: headline.kind,
    label: headline.label,
    tone: headline.tone,
    extraCount: others.length,                           // 0 for a single disposition; N for "+N"
    others: others.map((s) => s.label),                  // for the "+N" tooltip (folded states)
  };
}
